import { DEFAULT_AIRCRAFT_ID, type FlightLeg } from "./types";
import { money } from "./money";
import { routeNotesIndicateTimeShare } from "./rollupPersonal";

function normHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, "_");
}

function parseBool(s: string): boolean {
  const v = s.trim().toLowerCase();
  return ["1", "true", "t", "yes", "y"].includes(v);
}

function parseDate(s: string): string {
  const t = s.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
  const m = t.match(iso);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
  const u = t.match(us);
  if (u) {
    let mo = parseInt(u[1], 10);
    let da = parseInt(u[2], 10);
    let yr = parseInt(u[3], 10);
    if (yr < 100) yr += yr >= 70 ? 1900 : 2000;
    return `${yr}-${String(mo).padStart(2, "0")}-${String(da).padStart(2, "0")}`;
  }
  throw new Error(`Unrecognized date: ${JSON.stringify(s)}`);
}

function parseNum(s: string): number {
  const t = s.trim().replace(/,/g, "");
  if (!t) return 0;
  const n = Number(t);
  if (Number.isNaN(n)) throw new Error(`Invalid number: ${JSON.stringify(s)}`);
  return n;
}

/** Minimal CSV row parser (handles quoted fields with commas) */
export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let i = 0;
  let inQ = false;
  const pushCell = () => {
    row.push(cur);
    cur = "";
  };
  const pushRow = () => {
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQ = false;
        i++;
        continue;
      }
      cur += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQ = true;
      i++;
      continue;
    }
    if (c === ",") {
      pushCell();
      i++;
      continue;
    }
    if (c === "\r") {
      i++;
      continue;
    }
    if (c === "\n") {
      pushCell();
      pushRow();
      i++;
      continue;
    }
    cur += c;
    i++;
  }
  pushCell();
  if (row.some((cell) => cell.length > 0)) pushRow();
  return rows;
}

function col(
  colMap: Map<string, string>,
  ...aliases: string[]
): string | undefined {
  for (const a of aliases) {
    const k = normHeader(a);
    const orig = colMap.get(k);
    if (orig) return orig;
  }
  return undefined;
}

export function loadFlightCsv(text: string): FlightLeg[] {
  const normalized = text.replace(/^\uFEFF/, "");
  const table = parseCsvRows(normalized);
  if (table.length < 2) throw new Error("CSV must include a header and at least one row");

  const header = table[0];
  const colMap = new Map<string, string>();
  for (const h of header) {
    if (h) colMap.set(normHeader(h), h);
  }

  const cDate = col(colMap, "date", "flight_date");
  const cFrom = col(colMap, "from", "from_icao", "origin");
  const cTo = col(colMap, "to", "to_icao", "destination", "dest");
  const cPax = col(colMap, "pax", "passenger_count", "passengers");
  if (!cDate || !cFrom || !cTo || !cPax) {
    throw new Error(
      "CSV must include columns: date (or flight_date), from, to, pax (or passenger_count)"
    );
  }

  const cSeg = col(colMap, "segments", "segments_domestic", "domestic_segments");
  const cDomPax = col(
    colMap,
    "dom_pax",
    "passengers_domestic_segment_tax",
    "taxable_domestic_pax"
  );
  const cIntl = col(
    colMap,
    "intl_pax",
    "international_facility_passengers",
    "intl_passengers"
  );
  const cAkhi = col(colMap, "ak_hi_dep_pax", "alaska_hawaii_departure_pax");
  const cCharter = col(
    colMap,
    "charter_usd",
    "charter_amount",
    "taxable_transportation_usd",
    "fare_usd"
  );
  const cRural = col(colMap, "rural", "rural_airport_exempt");
  const cPersonal = col(colMap, "personal", "personal_use");
  const cSkip = col(colMap, "skip", "exclude_from_720", "exclude");
  const cLbs = col(colMap, "fuel_lbs", "fuel_pounds");
  const cGal = col(colMap, "fuel_gal", "fuel_gallons", "gallons");
  const cRoute = col(colMap, "route", "route_notes", "notes");
  const cHours = col(colMap, "flight_hours", "hours", "hobbs_hours");
  const cLandings = col(colMap, "landings", "landing_count");
  const cTakeoffs = col(colMap, "takeoffs", "takeoff_count");
  const cDepState = col(colMap, "departure_state", "from_state", "dep_state");
  const cTachStart = col(colMap, "tach_start", "tach_start_hr");
  const cTachEnd = col(colMap, "tach_end", "tach_end_hr");
  const cFuelPpg = col(colMap, "fuel_price_per_gallon", "fuel_usd_per_gal", "fuel_price");
  const cPaxNames = col(colMap, "passenger_names", "passengers_list", "passenger_list");
  const cCrewNames = col(colMap, "crew_names", "crew", "crew_list");
  const cTexasArrival = col(
    colMap,
    "texas_arrival",
    "texas_arrival_leg",
    "tx_arrival",
    "landing_in_texas"
  );
  const cAircraft = col(colMap, "aircraft", "tail", "n_number");

  const idx = (name: string) => header.indexOf(name);
  const legs: FlightLeg[] = [];

  for (let r = 1; r < table.length; r++) {
    const cells = table[r];
    if (!cells.some((c) => c.trim())) continue;

    const get = (cn: string | undefined) => {
      if (!cn) return "";
      const j = idx(cn);
      return j >= 0 && j < cells.length ? (cells[j] ?? "").trim() : "";
    };

    const rowNum = r + 1;
    const dateS = get(cDate);
    if (!dateS) throw new Error(`Row ${rowNum}: missing date`);

    let iso: string;
    try {
      iso = parseDate(dateS);
    } catch (e) {
      throw new Error(`Row ${rowNum}: ${(e as Error).message}`);
    }

    const fromIcao = get(cFrom).toUpperCase();
    const toIcao = get(cTo).toUpperCase();
    if (!fromIcao || !toIcao) {
      throw new Error(`Row ${rowNum}: from and to are required`);
    }

    const paxS = get(cPax);
    if (!paxS) throw new Error(`Row ${rowNum}: passenger count required`);
    const pax = parseInt(paxS, 10);
    if (Number.isNaN(pax)) {
      throw new Error(`Row ${rowNum}: invalid passenger count ${JSON.stringify(paxS)}`);
    }

    const segS = cSeg ? get(cSeg) : "1";
    let segments = segS ? parseInt(segS, 10) : 1;
    if (Number.isNaN(segments) || segments < 1) segments = 1;

    let domOverride: number | null = null;
    if (cDomPax && get(cDomPax)) {
      domOverride = parseInt(get(cDomPax), 10);
      if (Number.isNaN(domOverride)) {
        throw new Error(`Row ${rowNum}: invalid dom_pax`);
      }
    }

    const intlP =
      cIntl && get(cIntl) ? parseInt(get(cIntl), 10) || 0 : 0;
    const akhiP =
      cAkhi && get(cAkhi) ? parseInt(get(cAkhi), 10) || 0 : 0;

    const charter = cCharter && get(cCharter) ? money(parseNum(get(cCharter))) : 0;

    const rural =
      cRural && get(cRural) ? parseBool(get(cRural)) : false;
    const routeNotesRaw = cRoute ? get(cRoute) : "";
    const personalFromCol =
      cPersonal && get(cPersonal) ? parseBool(get(cPersonal)) : false;
    /** ForeFlight / worksheet “[Time share]” or “time share” in notes → personal even if column is false. */
    const personalUse =
      personalFromCol || routeNotesIndicateTimeShare(routeNotesRaw);
    const skip =
      cSkip && get(cSkip) ? parseBool(get(cSkip)) : false;

    let fuelLbs: number | null = null;
    if (cLbs && get(cLbs)) fuelLbs = parseNum(get(cLbs));

    let fuelGal: number | null = null;
    if (cGal && get(cGal)) fuelGal = parseNum(get(cGal));

    let flightHours: number | null = null;
    if (cHours && get(cHours)) {
      const hh = parseNum(get(cHours));
      if (!Number.isFinite(hh) || hh < 0) {
        throw new Error(`Row ${rowNum}: invalid flight_hours`);
      }
      flightHours = hh;
    }

    let landings: number | null = null;
    if (cLandings && get(cLandings)) {
      const ld = parseInt(get(cLandings), 10);
      if (Number.isNaN(ld) || ld < 0) {
        throw new Error(`Row ${rowNum}: invalid landings`);
      }
      landings = ld;
    }

    let takeoffs: number | null = null;
    if (cTakeoffs && get(cTakeoffs)) {
      const tk = parseInt(get(cTakeoffs), 10);
      if (Number.isNaN(tk) || tk < 0) {
        throw new Error(`Row ${rowNum}: invalid takeoffs`);
      }
      takeoffs = tk;
    }

    let departureState: string | null = null;
    if (cDepState && get(cDepState)) {
      departureState = get(cDepState).trim().toUpperCase().slice(0, 2) || null;
    }

    let tachStart: number | null = null;
    let tachEnd: number | null = null;
    if (cTachStart && get(cTachStart)) {
      const t = parseNum(get(cTachStart));
      if (!Number.isFinite(t)) throw new Error(`Row ${rowNum}: invalid tach_start`);
      tachStart = t;
    }
    if (cTachEnd && get(cTachEnd)) {
      const t = parseNum(get(cTachEnd));
      if (!Number.isFinite(t)) throw new Error(`Row ${rowNum}: invalid tach_end`);
      tachEnd = t;
    }

    let fuelPricePerGallon: number | null = null;
    if (cFuelPpg && get(cFuelPpg)) {
      const fp = parseNum(get(cFuelPpg));
      if (!Number.isFinite(fp) || fp < 0) {
        throw new Error(`Row ${rowNum}: invalid fuel price per gallon`);
      }
      fuelPricePerGallon = fp;
    }

    const passengerList =
      cPaxNames && get(cPaxNames) ? get(cPaxNames) : null;

    const crewList = cCrewNames && get(cCrewNames) ? get(cCrewNames) : null;

    const texasArrival =
      cTexasArrival && get(cTexasArrival) ? parseBool(get(cTexasArrival)) : false;

    const ac =
      cAircraft && get(cAircraft)
        ? get(cAircraft).trim().toUpperCase().slice(0, 12)
        : DEFAULT_AIRCRAFT_ID;

    legs.push({
      flightDate: iso,
      fromIcao,
      toIcao,
      passengerCount: pax,
      segmentsDomestic: segments,
      passengersDomesticSegmentTax: domOverride,
      internationalFacilityPassengers: intlP,
      alaskaHawaiiDomesticDeparturePassengers: akhiP,
      charterOrFareAmountUsd: charter,
      ruralAirportExempt: rural,
      personalUse,
      excludeFrom720: skip,
      fuelLbs,
      fuelGallonsReported: fuelGal,
      fuelPricePerGallon,
      aircraftId: ac,
      routeNotes: routeNotesRaw,
      tachStart,
      tachEnd,
      passengerList,
      crewList,
      texasArrival,
      flightHours,
      landings,
      takeoffs,
      departureState,
    });
  }

  return legs;
}
